import { useEffect, useRef, type ReactNode } from "react";

interface FocusTrapProps {
  children: ReactNode;
  isActive: boolean;
  onEscape?: () => void;
  restoreFocus?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement>;
}

export function FocusTrap({ 
  children, 
  isActive, 
  onEscape, 
  restoreFocus = true,
  initialFocusRef 
}: FocusTrapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousActiveElementRef = useRef<Element | null>(null);
  const previousTabIndexRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isActive || typeof document === "undefined") return;

    const container = containerRef.current;

    // Store the currently focused element
    previousActiveElementRef.current = document.activeElement;

    // Focus the initial element or first focusable element
    const focusInitialElement = () => {
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus();
        return;
      }

      const focusableElements = container?.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements && focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus({ preventScroll: true });
        return;
      }

      if (container) {
        previousTabIndexRef.current = container.getAttribute("tabindex");
        container.setAttribute("tabindex", "-1");
        container.focus({ preventScroll: true });
      }
    };

    // Small delay to ensure DOM is ready
    setTimeout(focusInitialElement, 0);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onEscape) {
        onEscape();
        return;
      }

      if (e.key === 'Tab') {
        const focusableElements = container?.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      
      // Restore focus to the previously focused element
      if (restoreFocus && previousActiveElementRef.current) {
        (previousActiveElementRef.current as HTMLElement).focus?.({ preventScroll: true });
      }

      if (container) {
        if (previousTabIndexRef.current === null) {
          container.removeAttribute("tabindex");
        } else {
          container.setAttribute("tabindex", previousTabIndexRef.current);
        }
        previousTabIndexRef.current = null;
      }
    };
  }, [isActive, onEscape, restoreFocus, initialFocusRef]);

  if (!isActive) return <>{children}</>;

  return (
    <div ref={containerRef} data-focus-trap>
      {children}
    </div>
  );
}
